import { graphemes } from '@/common/data/scopeAndSequence.json';
import { Grapheme } from '@/common/types/scopeAndSequence';
import { Attempt } from '@/common/types/session';
import {
  buildGraphemeStats,
  GraphemeStats,
} from '@/common/utils/itemAnalytics';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { Badge } from '@shadcn/components/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@shadcn/components/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shadcn/components/popover';
import { Progress } from '@shadcn/components/progress';
import { Separator } from '@shadcn/components/separator';
import { Skeleton } from '@shadcn/components/skeleton';
import { cn } from '@shadcn/lib/utils';
import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

interface DisplayAccuracies {
  green: [string, number | null][];
  amber: [string, number | null][];
  red: [string, number | null][];
  grey: [string, number | null][];
}

const pct = (n: number, d: number, round = true) => {
  if (d <= 0) return 0;
  const p = (n / d) * 100;
  return round ? Math.round(p) : p;
};

const progressColor = (progress: number | null) =>
  progress === null
    ? 'hsl(var(--accent))'
    : progress >= 90
      ? 'hsl(var(--rag-green))'
      : progress >= 75
        ? 'hsl(var(--rag-amber))'
        : 'hsl(var(--rag-red))';

interface GraphemeAccuracyProps {
  attempts: Attempt[];
  loading: boolean;
}
export default function GraphemeAccuracy({
  attempts,
  loading,
}: GraphemeAccuracyProps) {
  const statsByGraphemeId = useMemo(
    () => buildGraphemeStats(attempts),
    [attempts],
  );

  const grouped: DisplayAccuracies = useMemo(() => {
    const entries: Array<[string, number | null]> = [];
    for (const { id } of graphemes) {
      const stats = statsByGraphemeId.get(id);
      if (!stats || !stats.totalAttempts) {
        entries.push([id, null]);
        continue;
      }
      const accuracy = pct(stats.totalCorrect, stats.totalAttempts);
      entries.push([id, accuracy]);
    }

    entries.sort((a, b) => {
      const [, aa] = a;
      const [, bb] = b;
      if (aa === null && bb === null) return 0;
      if (aa === null) return -1;
      if (bb === null) return 1;
      return aa - bb;
    });

    const res: DisplayAccuracies = { red: [], amber: [], green: [], grey: [] };
    for (const [id, acc] of entries) {
      if (acc === null) {
        res.grey.push([id, null]);
      } else if (acc >= 90) {
        res.green.push([id, acc]);
      } else if (acc >= 75) {
        res.amber.push([id, acc]);
      } else {
        res.red.push([id, acc]);
      }
    }
    return res;
  }, [statsByGraphemeId]);

  return (
    <Card className="w-full h-full relative">
      <CardHeader>
        <CardTitle>Grapheme Accuracy</CardTitle>
        <CardDescription className="flex flex-col gap-2">
          <span>The accuracy of the learner's grapheme recognitions</span>
        </CardDescription>
      </CardHeader>
      {loading ? (
        <CardContent className="flex flex-col gap-2">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(75px,1fr))] gap-2">
            {Array.from({ length: 10 }).map((_, j) => (
              <Skeleton key={j} className="aspect-square" />
            ))}
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(75px,1fr))] gap-2">
            {Array.from({ length: 18 }).map((_, j) => (
              <Skeleton key={j} className="aspect-square" />
            ))}
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(75px,1fr))] gap-2">
            {Array.from({ length: 28 }).map((_, j) => (
              <Skeleton key={j} className="aspect-square" />
            ))}
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(75px,1fr))] gap-2">
            {Array.from({ length: 35 }).map((_, j) => (
              <Skeleton key={j} className="aspect-square" />
            ))}
          </div>
        </CardContent>
      ) : (
        <CardContent className="flex flex-col gap-2">
          <div className="grid grid-cols-[repeat(auto-fill,minmax(75px,1fr))] gap-2">
            {grouped.red.map(([itemId]) => (
              <ContentItem
                key={itemId}
                itemId={itemId}
                stats={statsByGraphemeId.get(itemId) || null}
              />
            ))}
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(75px,1fr))] gap-2">
            {grouped.amber.map(([itemId]) => (
              <ContentItem
                key={itemId}
                itemId={itemId}
                stats={statsByGraphemeId.get(itemId) || null}
              />
            ))}
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(75px,1fr))] gap-2">
            {grouped.green.map(([itemId]) => (
              <ContentItem
                key={itemId}
                itemId={itemId}
                stats={statsByGraphemeId.get(itemId) || null}
              />
            ))}
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(75px,1fr))] gap-2">
            {grouped.grey.map(([itemId]) => (
              <ContentItem
                key={itemId}
                itemId={itemId}
                stats={statsByGraphemeId.get(itemId) || null}
              />
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

interface ContentItemProps {
  itemId: string;
  stats: GraphemeStats | null;
}
function ContentItem({ itemId, stats }: ContentItemProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const dialogOpenTime = useRef<number | null>(null);

  if (!itemId.startsWith('grapheme-')) return null;
  const grapheme = (graphemes as Grapheme[]).find((g) => g.id === itemId);
  if (!grapheme || !stats) return null;
  const overallPct = stats.totalAttempts
    ? pct(stats.totalCorrect, stats.totalAttempts)
    : null;
  const isolatedPct = pct(stats.isolatedCorrect, stats.isolatedCount);
  const inWordPct = pct(stats.inWordCorrect, stats.inWordCount);

  const handleDialogOpenChange = (open: boolean) => {
    if (open) {
      // Dialog opening
      dialogOpenTime.current = Date.now();
      trackMixpanelEvent('Grapheme Detail Opened', {
        graphemeId: itemId,
        spelling: grapheme.spelling,
        accuracy: overallPct,
        totalAttempts: stats.totalAttempts,
      });
    } else {
      // Dialog closing
      const timeSpentMs = dialogOpenTime.current
        ? Date.now() - dialogOpenTime.current
        : 0;
      trackMixpanelEvent('Grapheme Detail Closed', {
        graphemeId: itemId,
        timeSpentMs,
      });
      dialogOpenTime.current = null;
    }
    setDialogOpen(open);
  };

  const handleTileClick = () => {
    trackMixpanelEvent('Grapheme Tile Clicked', {
      graphemeId: itemId,
      spelling: grapheme.spelling,
      accuracy: overallPct,
      totalAttempts: stats.totalAttempts,
    });
    setDialogOpen(true);
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger
          className={cn(
            'flex flex-col gap-2 rounded-md p-2 text-white aspect-square justify-center items-center min-w-16 border-2 border-transparent box-border',
            overallPct === null
              ? 'bg-gray-100 text-gray-600'
              : overallPct >= 90
                ? 'bg-rag-green'
                : overallPct >= 75
                  ? 'bg-rag-amber'
                  : 'bg-rag-red',
            popoverOpen && 'border-2 border-blue-500',
          )}
          onMouseEnter={() => setPopoverOpen(true)}
          onMouseLeave={() => setPopoverOpen(false)}
          onClick={handleTileClick}
        >
          <p className="text-sm font-medium">
            {grapheme.spelling} /{grapheme.pronunciation}/
          </p>
          {overallPct !== null && <p className="text-normal">{overallPct}%</p>}
        </PopoverTrigger>
        <PopoverContent align="start">
          <ItemDetails
            grapheme={grapheme}
            stats={stats}
            overallPct={overallPct}
            isolatedPct={isolatedPct}
            inWordPct={inWordPct}
          />
        </PopoverContent>
      </Popover>

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between mr-8 text-base sm:text-lg font-semibold">
              <span>
                {grapheme.spelling} /{grapheme.pronunciation}/
              </span>
              <span>
                {stats.totalCorrect}/{stats.totalAttempts} ({overallPct}%)
              </span>
            </DialogTitle>

            <div className="flex mt-2">
              <Progress
                value={overallPct}
                color={progressColor(overallPct)}
                className="h-2 flex-1 bg-accent"
              />
            </div>
          </DialogHeader>

          <ItemDetails
            grapheme={grapheme}
            stats={stats}
            overallPct={overallPct}
            isolatedPct={isolatedPct}
            inWordPct={inWordPct}
            inDialog
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

interface ItemDetailsProps {
  grapheme: Grapheme;
  stats: GraphemeStats;
  overallPct: number | null;
  isolatedPct: number;
  inWordPct: number;
  inDialog?: boolean;
}
function ItemDetails({
  grapheme,
  stats,
  overallPct,
  isolatedPct,
  inWordPct,
  inDialog,
}: ItemDetailsProps) {
  return (
    <div className="flex flex-col text-sm">
      {!inDialog && (
        <>
          <h3 className="flex items-center justify-between text-base sm:text-lg font-semibold">
            <span>
              {grapheme.spelling} /{grapheme.pronunciation}/
            </span>
            <span>
              {stats.totalCorrect}/{stats.totalAttempts} ({overallPct}%)
            </span>
          </h3>

          <div className="flex mt-2">
            <Progress
              value={overallPct}
              color={progressColor(overallPct)}
              className="h-2 flex-1 bg-accent"
            />
          </div>
        </>
      )}

      <Separator className="my-2" />

      <div className="flex flex-col gap-2">
        {/* Two sections: Isolated / In-Word */}
        <div className="flex flex-col gap-4">
          <Section
            title="Isolated Exposures"
            exposures={stats.isolatedCount}
            correct={stats.isolatedCorrect}
            percent={isolatedPct}
            hasAttempts={!!stats.totalAttempts}
          />
          <Section
            title="In-Word Exposures"
            exposures={stats.inWordCount}
            correct={stats.inWordCorrect}
            percent={inWordPct}
            hasAttempts={!!stats.totalAttempts}
          />
        </div>

        <Separator className="mt-2" />

        {/* Recent words */}
        {stats.recentWords.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="font-semibold">Recent Words</span>
            <div className="flex flex-wrap gap-2">
              {stats.recentWords.map((w) => (
                <Badge key={w} variant="secondary" className="text-sm">
                  {w}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({
  title,
  exposures,
  correct,
  percent,
  hasAttempts,
}: {
  title: string;
  exposures: number;
  correct: number;
  percent: number;
  hasAttempts: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 mt-2">
      <div className="flex items-center justify-between gap-1">
        <span className="font-medium">{title}</span>
        <span className="flex items-center gap-2 tabular-nums text-sm min-w-[3.5rem] text-right">
          <span>{percent}% Correct</span>
          <ProgressCircle percent={hasAttempts ? percent : null} />
        </span>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 gap-2">
        <StatTile label="Exposures" value={exposures.toLocaleString()} />
        <StatTile label="Correct" value={`${correct.toLocaleString()}`} />
      </div>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-md border p-1 flex flex-col items-center gap-1">
      <span className="text-xl sm:text-2xl font-semibold leading-none">
        {value}
      </span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

interface ProgressCircleProps {
  percent: number | null;
}
const ProgressCircle = ({ percent }: ProgressCircleProps) => {
  // Convert mastery percentage to radians
  const percentage = Math.min(Math.max(percent || 0, 0), 100) / 100;
  const degrees = percentage * 360;
  const radians = (degrees * Math.PI) / 180;

  // Calculate the path for the pie slice
  const outerRadius = 11;
  const innerRadius = 8;
  const center = { x: 12, y: 12 };

  // Calculate end point of arc using the inner radius
  const endX = center.x + innerRadius * Math.cos(radians);
  const endY = center.y + innerRadius * Math.sin(radians);

  // Generate the SVG path for the pie slice
  const path =
    percentage === 0
      ? ''
      : percentage === 1
        ? `M ${center.x},${center.y} 
         m -${innerRadius},0 
         a ${innerRadius},${innerRadius} 0 1,1 ${innerRadius * 2},0 
         a ${innerRadius},${innerRadius} 0 1,1 -${innerRadius * 2},0`
        : `M ${center.x},${center.y}
         L ${center.x + innerRadius},${center.y}
         A ${innerRadius},${innerRadius} 0 ${
           percentage > 0.5 ? 1 : 0
         },1 ${endX},${endY}
         L ${center.x},${center.y}`;

  return (
    <CircleContainer>
      <ProgressSVG viewBox="0 0 24 24">
        <OutlineCircle
          cx="12"
          cy="12"
          r={outerRadius}
          $color={progressColor(percent)}
        />
        <ProgressSlice d={path} $color={progressColor(percent)} />
      </ProgressSVG>
    </CircleContainer>
  );
};

const CircleContainer = styled.div`
  height: 18px;
  width: 18px;
`;

const ProgressSVG = styled.svg`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
`;

const OutlineCircle = styled.circle<{ $color: string }>`
  fill: none;
  stroke-width: 2;
  stroke: ${({ $color }) => $color};
`;

const ProgressSlice = styled.path<{ $color: string }>`
  fill: ${({ $color }) => $color};
`;
